import { useState } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2 } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { fadeIn, warmEase } from '@/shared/motion/motionPresets';
import { ImportHistorySection } from './ImportHistorySection';
import { computeFailedCsvFilename, downloadFailureCsv } from './downloadFailureCsv';
import type { BulkReferralResponseDto } from '../../../types/bulk-referral.type';

type SuccessStageProps = {
  summary: BulkReferralResponseDto['summary'];
  failureCsv?: string | null;
  fileName?: string;
  onUploadAnother: () => void;
};

const buildTitle = (errorRows: number, resolvedRows: number) => {
  if (errorRows === 0) return 'Import complete';
  if (resolvedRows === 0) return 'Nothing imported';
  return 'Import complete — some rows need a retry';
};

const buildCounts = (summary: BulkReferralResponseDto['summary']) => {
  const counts = [
    { label: 'Referrals written', value: summary.resolvedRows },
    { label: 'Care seeker contacts created', value: summary.newContacts },
    { label: 'Invitation emails sent', value: summary.invitesToSend },
  ];
  if (summary.errorRows > 0) {
    counts.push({ label: 'Rows to retry', value: summary.errorRows });
  }
  return counts;
};

export const SuccessStage = ({ summary, failureCsv, fileName, onUploadAnother }: SuccessStageProps) => {
  const [hasDownloaded, setHasDownloaded] = useState(false);
  const showDownload = summary.errorRows > 0 && !!failureCsv;
  const failedFilename = computeFailedCsvFilename(fileName ?? 'referrals.csv');

  const handleDownload = () => {
    if (!failureCsv) return;
    downloadFailureCsv(failureCsv, failedFilename);
    setHasDownloaded(true);
  };

  const handleUploadAnotherClick = () => {
    if (showDownload && !hasDownloaded) {
      if (!window.confirm('Leave without downloading the failures CSV?')) return;
    }
    onUploadAnother();
  };

  return (
    <>
      <motion.div {...fadeIn} className="flex flex-col items-center gap-4 px-6 py-10 text-center">
        <CheckCircle2 className="h-10 w-10 text-emerald-600" aria-hidden />
        <h3 className="text-xl font-semibold text-gray-900">
          {buildTitle(summary.errorRows, summary.resolvedRows)}
        </h3>

        <div className="flex flex-wrap justify-center gap-3">
          {buildCounts(summary).map((count, index) => (
            <motion.div
              key={count.label}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, ease: warmEase, delay: 0.1 + index * 0.08 }}
              className="w-48 rounded-xl border border-gray-200 bg-white p-4"
            >
              <p className="text-2xl font-semibold text-gray-900">{count.value}</p>
              <p className="mt-1 text-xs text-gray-600">{count.label}</p>
            </motion.div>
          ))}
        </div>

        {showDownload && (
          <div className="flex flex-col items-center gap-1">
            <Button onClick={handleDownload} className="bg-[#026857] hover:bg-[#025045]">
              Download {failedFilename}
            </Button>
            <p className="text-xs text-gray-500">
              {hasDownloaded ? `Saved ${failedFilename}` : 'This file is only available on this screen.'}
            </p>
          </div>
        )}

        <div className="mt-2 flex items-center gap-4">
          <Button variant="outline" onClick={handleUploadAnotherClick}>
            Upload another file
          </Button>
          <a href="#import-history" className="text-sm font-medium text-[#026857] hover:underline">
            View import history
          </a>
        </div>
      </motion.div>

      <ImportHistorySection />
    </>
  );
};
